"use client";

import { Code } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppConfig } from "@/lib/config";
import { useApi } from "@/lib/fetch-client";

import { useI18n } from "@llmgateway/i18n/react";

function Newsletter() {
	const [email, setEmail] = useState("");
	const api = useApi();
	const { t } = useI18n();
	const subscribe = api.useMutation("post", "/public/newsletter/subscribe");

	return (
		<div className="relative rounded-2xl border border-border/60 bg-gradient-to-b from-muted/40 to-muted/20 overflow-hidden mb-12">
			<div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
			<div className="relative px-6 py-10 sm:px-10 sm:py-12">
				{subscribe.isSuccess ? (
					<div className="flex flex-col items-center gap-4 text-center py-2">
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10 ring-1 ring-green-500/20">
							<svg
								className="h-6 w-6 text-green-500"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								strokeWidth={2}
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M5 13l4 4L19 7"
								/>
							</svg>
						</div>
						<div className="space-y-1">
							<h3 className="text-xl font-bold">
								{t("footer.newsletter.successTitle")}
							</h3>
							<p className="text-sm text-muted-foreground max-w-sm mx-auto">
								{subscribe.data?.message ??
									t("footer.newsletter.successDescription")}
							</p>
						</div>
					</div>
				) : (
					<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 md:gap-12">
						<div className="flex-1 max-w-lg space-y-3">
							<p className="text-xs font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400">
								{t("footer.newsletter.eyebrow")}
							</p>
							<h3 className="text-xl sm:text-2xl font-bold tracking-tight">
								{t("footer.newsletter.title")}
							</h3>
							<p className="text-sm text-muted-foreground leading-relaxed max-w-md">
								{t("footer.newsletter.description")}
							</p>
						</div>
						<div className="w-full md:w-auto md:min-w-[300px]">
							<form
								className="flex flex-col gap-3"
								onSubmit={(e) => {
									e.preventDefault();
									if (subscribe.isPending) {
										return;
									}
									subscribe.mutate({ body: { email } });
								}}
							>
								<Input
									type="email"
									placeholder="you@company.com"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
									className="h-11 rounded-xl bg-background/80"
								/>
								<Button
									type="submit"
									disabled={subscribe.isPending}
									className="h-11 rounded-xl"
								>
									{subscribe.isPending
										? t("footer.newsletter.submitting")
										: t("footer.newsletter.submit")}
								</Button>
								<p className="text-[11px] text-muted-foreground/60 text-center">
									{t("footer.newsletter.noSpam")}
								</p>
							</form>
							{subscribe.isError && (
								<p className="mt-2 text-sm text-destructive text-center">
									{t("footer.newsletter.error")}
								</p>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export function Footer() {
	const config = useAppConfig();
	const { t } = useI18n();

	return (
		<footer className="relative border-t py-12 px-4">
			<div className="container mx-auto max-w-5xl">
				<Newsletter />

				<div className="flex flex-col md:flex-row md:justify-between md:items-start gap-8">
					<div>
						<Link href="/" className="flex items-center gap-2">
							<div className="flex h-7 w-7 items-center justify-center rounded-lg bg-foreground text-background">
								<Code className="h-4 w-4" />
							</div>
							<span className="font-semibold">DevPass</span>
						</Link>
					</div>

					<div className="grid grid-cols-2 gap-8 text-muted-foreground sm:grid-cols-3">
						<div>
							<h3 className="text-sm font-semibold mb-4 text-foreground">
								{t("footer.sections.product")}
							</h3>
							<ul className="space-y-2">
								<li>
									<Link
										href="/coding-models"
										className="text-sm hover:underline underline-offset-4 hover:text-foreground"
									>
										{t("footer.links.codingModels")}
									</Link>
								</li>
								<li>
									<Link
										href="/pricing"
										className="text-sm hover:underline underline-offset-4 hover:text-foreground"
									>
										{t("footer.links.pricing")}
									</Link>
								</li>
								<li>
									<Link
										href="/compare"
										className="text-sm hover:underline underline-offset-4 hover:text-foreground"
									>
										{t("footer.links.comparePlans")}
									</Link>
								</li>
								<li>
									<a
										href={`${config.uiUrl}/models`}
										rel="noopener"
										target="_blank"
										className="text-sm hover:underline underline-offset-4 hover:text-foreground"
									>
										{t("footer.links.allModels")}
									</a>
								</li>
								<li>
									<a
										href={config.uiUrl}
										rel="noopener"
										target="_blank"
										className="text-sm hover:underline underline-offset-4 hover:text-foreground"
									>
										doteb
									</a>
								</li>
							</ul>
						</div>
						<div>
							<h3 className="text-sm font-semibold mb-4 text-foreground">
								{t("footer.sections.resources")}
							</h3>
							<ul className="space-y-2">
								<li>
									<a
										href={config.docsUrl}
										target="_blank"
										rel="noopener"
										className="text-sm hover:underline underline-offset-4 hover:text-foreground"
									>
										{t("footer.links.documentation")}
									</a>
								</li>
								<li>
									<a
										href={`${config.uiUrl}/integrations`}
										target="_blank"
										rel="noopener"
										className="text-sm hover:underline underline-offset-4 hover:text-foreground"
									>
										{t("footer.links.integrations")}
									</a>
								</li>
								<li>
									<a
										href={`${config.uiUrl}/blog`}
										target="_blank"
										rel="noopener"
										className="text-sm hover:underline underline-offset-4 hover:text-foreground"
									>
										{t("footer.links.blog")}
									</a>
								</li>
							</ul>
						</div>
					</div>
				</div>

				<div className="border-t border-border/50 pt-8 mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
					<p className="text-muted-foreground text-sm">
						&copy; {new Date().getFullYear()} doteb.{" "}
						{t("footer.copyright")}
					</p>
					<div className="flex items-center gap-6">
						<Link
							href="/legal/privacy"
							className="text-sm text-muted-foreground hover:underline underline-offset-4 hover:text-foreground"
						>
							{t("footer.links.privacyPolicy")}
						</Link>
						<Link
							href="/legal/terms"
							className="text-sm text-muted-foreground hover:underline underline-offset-4 hover:text-foreground"
						>
							{t("footer.links.termsOfUse")}
						</Link>
					</div>
				</div>
			</div>
		</footer>
	);
}
